import * as React from 'react';
import { Input, Select, Switch } from '@ixicare/ui';
import { t } from '~i18n';
import { Field, FieldValue, FieldValues, same, SubProfile, isOptionObject } from '../schema';
import FieldLabel from './FieldLabel';
import NumberFieldControl from './NumberFieldControl';
import StatusBlockControl from './StatusBlockControl';
import PolygonControl from './PolygonControl';
import { isFieldEnabled } from '../utils/visibility';
import { applySubProfile, deriveSubProfile } from '../utils/subProfile';
import { GatingContext, appliesToDeviceType, canViewField } from '../utils/gating';
import { CheckboxChoice, ChoiceList } from '~components/Choice';
import { useChoiceField } from './useChoiceField';
import { ControlOverrides } from './controls';
import FieldWrapper, { DifferingKeyDetail } from './FieldWrapper';

interface Props {
  field: Field;
  values: FieldValues;
  initialValue: FieldValue;
  initialValues: FieldValues;
  error?: string;
  onChange: (id: string, value: FieldValue) => void;
  gating: GatingContext;
  differingDetail?: Map<string, DifferingKeyDetail[]>;
  inheritedDetail?: Map<string, DifferingKeyDetail[]>;
  /**
   * Fields the form holds at their current value, naming them rather than deriving it.
   *
   * `enabled_if` covers a field disabled by one other field's value; this is for a rule the
   * definition cannot state -- an allow list forces outdoor cover on, which depends on two fields
   * and is a correction the page makes, not something the service describes. Without it the
   * control looks clickable and silently snaps back, which reads as a broken checkbox.
   */
  disabledFields?: string[];
  readOnly?: boolean;
  dimInherited?: boolean;
  // Stage this field into or out of the layer the form writes. Absent means the form has no such
  // layer, or may not write it.
  onToggleStored?: (field: Field) => void;
  deviceType?: string;
  // Renders a field this control reveals, inside it. The section hands this down rather than
  // laying the field out itself: a revealed field belongs within its parent control, not beside
  // it in the grid.
  renderRevealed?: (ids: string[]) => React.ReactNode;
  showFlags?: boolean;
  /** Controls the definition cannot describe; see `controls.ts`. */
  controls?: ControlOverrides;
}

// Bare options carry no props, so they are never part of a sub-profile.
const asSubProfile = (field: Field): SubProfile => ({
  label_key: field.title,
  options: (field.options ?? []).filter(isOptionObject).map((o) => ({
    value: String(o.value),
    label_key: o.label_key ?? String(o.value),
    props: o.props,
  })),
});

const controlId = (field: Field): string => `${field.id}-control`;

const toSelectOption = (o: NonNullable<Field['options']>[number]): { value: string; label: string } =>
  typeof o === 'string'
    ? { value: o, label: o }
    : {
        value: String(o.value),
        label: o.label_key ? t(o.label_key) : String(o.value),
      };

const FieldRenderer: React.FC<Props> = ({
  field,
  values,
  initialValue,
  initialValues,
  error,
  onChange,
  gating,
  differingDetail,
  inheritedDetail,
  readOnly,
  disabledFields,
  dimInherited,
  onToggleStored,
  deviceType,
  renderRevealed,
  showFlags,
  controls,
}) => {
  const value = values[field.id];
  const choice = useChoiceField({ field, value, deviceType, onChange, renderRevealed });

  if (!canViewField(field, gating)) return null;
  if (!appliesToDeviceType(field.only_for, deviceType)) return null;

  // Read-only and "this field does not apply right now" reach the controls the same way; the
  // controls only need to know they cannot be typed in.
  const disabled = readOnly || disabledFields?.includes(field.id) || !isFieldEnabled(field, values);
  const label = <FieldLabel field={field} />;
  const changed = !same(value, initialValue) && initialValue !== undefined;

  let control: React.ReactNode = null;

  const supplied = controls?.[field.type];
  if (supplied) {
    control = supplied({ field, value, values, label, disabled, onChange });
  } else {
    switch (field.type) {
      case 'TextField':
      case 'PasswordField':
      case 'TextArea':
      case 'CodeBlock':
        control = (
          <Input
            label={label}
            type={field.type === 'PasswordField' ? 'password' : 'text'}
            value={value == null ? '' : String(value)}
            placeholder={field.placeholder ?? ''}
            onValueChange={(v) => onChange(field.id, v)}
            disabled={disabled}
          />
        );
        break;
      case 'NumberField':
        control = (
          <NumberFieldControl
            field={field}
            value={value}
            label={label}
            onChange={(v) => onChange(field.id, v)}
            disabled={disabled}
          />
        );
        break;
      case 'DropDown': {
        const isPreset = !field.synthetic && (field.options ?? []).some((o) => isOptionObject(o) && o.props);
        if (isPreset) {
          const sub = asSubProfile(field);
          const selectedValue = deriveSubProfile(sub, values);
          control = (
            <Select
              label={label}
              value={selectedValue == null ? null : String(selectedValue)}
              options={(field.options ?? []).map(toSelectOption)}
              onChange={(v) => applySubProfile(sub, v, initialValues, onChange)}
              placeholder={field.placeholder}
              disabled={disabled}
            />
          );
          break;
        }

        const vals = (field.options ?? []).map((o) => (typeof o === 'string' ? o : o.value));

        const selected = vals.find((v) => v === value) ?? value;

        control = (
          <Select
            label={label}
            value={selected == null ? null : String(selected)}
            options={(field.options ?? []).map(toSelectOption)}
            onChange={(v) => {
              // Recover the typed value (number/boolean) the option declared.
              const original = vals.find((val) => String(val) === v);
              onChange(field.id, original !== undefined ? original : v);
            }}
            placeholder={field.placeholder}
            disabled={disabled}
          />
        );
        break;
      }
      case 'ToggleSwitch':
        control = (
          <Switch label={label} checked={value === true} onChange={(v) => onChange(field.id, v)} disabled={disabled} />
        );
        break;
      case 'CheckboxChoice':
        control = (
          <CheckboxChoice
            id={controlId(field)}
            title={t(field.title, field.title)}
            description={field.description_key ? t(field.description_key) : undefined}
            checked={choice.checked}
            disabled={disabled}
            onCheckedChange={choice.setChecked}
          >
            {choice.choices.length > 0 && (
              <ChoiceList
                name={field.id}
                label={t(field.title, field.title)}
                value={choice.selected}
                choices={choice.choices}
                onChange={choice.choose}
              />
            )}
            {renderRevealed?.(field.reveals ?? [])}
          </CheckboxChoice>
        );
        break;
      case 'ChoiceList':
        control = (
          <ChoiceList
            name={field.id}
            label={t(field.title, field.title)}
            value={choice.selected}
            choices={choice.choices}
            onChange={choice.choose}
          />
        );
        break;
      case 'StatusBlock':
        control = <StatusBlockControl field={field} label={label} />;
        break;
      case 'Polygon':
        control = <PolygonControl value={value} label={label} />;
        break;
    }
  }

  return (
    <FieldWrapper
      field={field}
      error={error}
      changed={changed && !field.highlight}
      bulk={differingDetail !== undefined}
      differing={differingDetail?.has(field.id) ?? false}
      differingDetail={differingDetail?.get(field.id)}
      inheritedDetail={inheritedDetail?.get(field.id)}
      dimInherited={dimInherited}
      onToggleStored={onToggleStored && field.enabled !== false ? () => onToggleStored(field) : undefined}
      showFlags={showFlags}
    >
      {control}
    </FieldWrapper>
  );
};

export default FieldRenderer;
